import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { deepClone } from '../../utils';
import Box from '../box';
import { Form } from '../form';
import { withFormContext } from '../form/hocs';
import {
	FormContextProps,
	ValidationRule,
	getValueByName,
	setValueByName,
} from '../form/shared';


export type FormRepeaterProps<T> = {
	name: string | Array<string>;
	readonly?: boolean;
	defaultFormObject?: T;
	addCustomFormValidationRule?: ValidationRule<T>;
	component?: React.ElementType<any>;
	itemComponent?: React.ElementType<any>;
	scrollContainer?: HTMLElement | 'window';
	IDKey: string;
	renderAddItemTrigger: (options: {
		handleAddItem: () => void;
		handleReplaceItems: (items: Array<T>) => void;
	}) => React.ReactNode;
	onCheckBeforeAddItem?: (cb: () => void) => void;
	onChangeItem?: (item: T, index: number) => void;
	onAddItem?: (item: T, idx: number) => void;
	onSetItemRootRef?: (ref: HTMLElement) => void;
	children: (options: {
		formObject: any;
		idx: number;
		name: string | Array<string>;
		item: T;
		handleRemoveItem: () => void;
	}) => React.ReactNode | React.ReactNode;
} & Partial<FormContextProps<T>>;

export type FormRepeaterComponent<T = any> = React.SFC<FormRepeaterProps<T>>;

class Repeater<T> extends React.Component<FormRepeaterProps<T>> {
	static displayName = 'FormRepeater';
	static defailtProps = {
		IDKey: 'ID',
		component: 'div',
		itemComponent: 'div',
		onSetItemRootRef: () => {},
	};
	formRefList: Array<any> = [];
	formResults = [];
	nextID = -1;

	componentDidMount() {
		const {
			addValidationRule,
			addCustomFormValidationRule
		} = this.props;

		typeof addCustomFormValidationRule === 'function' && addValidationRule(addCustomFormValidationRule);
	}

	componentWillUnmount() {
		const { removeValidationRule, addCustomFormValidationRule } = this.props;
	
		typeof addCustomFormValidationRule === 'function' && removeValidationRule(addCustomFormValidationRule);
	}

	handleAddFormItem = () => {
		const {
			name,
			getFormObject,
			setFormObject,
			defaultFormObject,
			IDKey,
			onCheckBeforeAddItem,
			onAddItem,
		} = this.props;
		const formObject = getFormObject();
		const value = getValueByName(name, formObject) as Array<T>;
		const item = { ...deepClone(defaultFormObject), [IDKey]: this.getNextID() } as T;

		if (typeof onCheckBeforeAddItem === 'function') {
			onCheckBeforeAddItem(() => {
				value.push(item);
				setFormObject(formObject);
				onAddItem && onAddItem(item, value.length - 1);
			});
		} else {
			value.push(item);
			setFormObject(formObject);
			onAddItem && onAddItem(item, value.length - 1);
		}
	};

	handleReplaceItems = (items: Array<T>) => {
		const {
			name,
			getFormObject,
			setFormObject,
			IDKey,
		} = this.props;
		const formObject = getFormObject();
		const value = items.map((x, idx) => ({ ...x, [IDKey]: -(idx + 1) }));

		setValueByName(value, name, formObject);
		setFormObject(formObject);
	};

	getNextID = () => {
		const {
			name,
			getFormObject,
			IDKey,
		} = this.props;
		const formObject = getFormObject();
		const value = getValueByName(name, formObject) as Array<T>;
		const IDs = value.map((x, idx) => typeof x[IDKey] === 'string' ? idx : x[IDKey]);

		this.nextID = value.length > 0 ? Math.min(...IDs) - 1 : -1;
		this.nextID = this.nextID < 0 ? this.nextID : -1;

		return this.nextID--;
	};

	handleRemoveItem = (item: T) => {
		const {
			name,
			getFormObject,
			setFormObject,
			IDKey,
		} = this.props;
		const formObject = getFormObject();
		const value = getValueByName(name, formObject) as Array<T>;
		const newValue = value.filter(x => x[IDKey] !== item[IDKey]);

		setValueByName(newValue, name, formObject);
		setFormObject(formObject);
	};

	handleChangeItem = (idx: number) => (item: T) => {
		const {
			name,
			getFormObject,
			setFormObject,
			onChangeItem
		} = this.props;
		const formObject = getFormObject();
		const value = getValueByName(name, formObject) as Array<T>;

		value[idx] = item;

		setValueByName(value, name, formObject);
		setFormObject(formObject);

		typeof onChangeItem === 'function' && onChangeItem(item, idx);
	};

	renderChildren = () => {
		const {
			getFormObject,
			name,
			readonly,
			IDKey,
			addValidationRule,
			removeValidationRule,
			scrollContainer,
			itemComponent,
			onSetItemRootRef,
		} = this.props;
		const formObject = getFormObject();
		const zone = getValueByName(name, formObject) as Array<T>;

		if (zone && zone.length > 0) {
			return zone.map((item, idx) => {
				return (
					<FormRepeaterItem
						key={item[IDKey]}
						item={item}
						IDKey={IDKey}
						readonly={readonly}
						component={itemComponent}
						scrollContainer={scrollContainer}
						data={zone}
						getFormObject={getFormObject}
						onChange={this.handleChangeItem(idx)}
						onRemove={this.handleRemoveItem}
						addValidationRule={addValidationRule}
						removeValidationRule={removeValidationRule}
						onSetRootRef={onSetItemRootRef}>
						{
							typeof this.props.children === 'function'
								? this.props.children({
										formObject,
										idx,
										name,
										item,
										handleRemoveItem: () => this.handleRemoveItem(item),
									})
								: this.props.children
						}
					</FormRepeaterItem>
				);
			});
		}

		return null;
	};


	render() {
		const { component, renderAddItemTrigger } = this.props;

		return (
			<Box component={component} position='relative' width='100%'>
				{this.renderChildren()}
				{
					typeof renderAddItemTrigger === 'function' &&
					renderAddItemTrigger({
						handleAddItem: this.handleAddFormItem,
						handleReplaceItems: this.handleReplaceItems,
					})
				}
			</Box>
		);
	}
}

type FormRepeaterItemProps<S,T> = {
	item: T;
	IDKey: string;
	readonly: boolean;
	component: React.ElementType;
	scrollContainer: HTMLElement | 'window';
	data: any;
	getFormObject: () => S;
	onChange: (item: T) => void;
	onRemove: (item: T) => void;
	addValidationRule: (rule: ValidationRule<T>) => void;
	removeValidationRule: (rule: ValidationRule<T>) => void;
	onSetRootRef?: (ref: HTMLElement) => void;
};

class FormRepeaterItem<S,T> extends React.Component<FormRepeaterItemProps<S,T>> {
	static displayName = 'FormRepeaterItem';
	static defaultProps = {
		addValidationRule: () => {},
		removeValidationRule: () => {},
		onSetRootRef: () => {},
	};
	formRef = null;
	validationRile = {
		errorText: '',
		isAsync: true,
		fn: () => new Promise<boolean>(resolve => {
			const formObject = this.formRef.getFormObject();
			this.formRef.validateForm(formObject, (isValid: boolean) => {
				resolve(isValid);
			});
		}),
	};

	componentDidMount() {
		this.props.addValidationRule(this.validationRile);
	}

	componentWillUnmount() {
		this.props.removeValidationRule(this.validationRile);
	}

	setFormRef = (ref) => {
		const domElement = ReactDOM.findDOMNode(ref) as HTMLElement;

		this.formRef = ref;
		this.props.onSetRootRef(domElement);
	};

	handleChange = (value: React.SyntheticEvent | T) => {
		const { IDKey } = this.props;
		const event = value as React.SyntheticEvent;
		const item = value as T
		const isSynthEvent = Boolean(event.eventPhase);
		
		if (!isSynthEvent) {
			item[IDKey] && this.props.onChange(item);
		}
	};

	handleRemove = (item: T) => this.props.onRemove(item);

	render() {
		const { item, component, data } = this.props;

		return (
			<Form
				ref={this.setFormRef}
				formObject={item}
				data={data}
				component={component}
				onChange={this.handleChange}>
				{this.props.children}
			</Form>
		);
	}
}

const FormRepeaterBase = withFormContext<Omit<FormRepeaterProps<any>, 'name'>, any>(Repeater);

const FormRepeater = {
	Base: FormRepeaterBase,
}

export default FormRepeater;
